import { LogoBackdrop } from "@freightsimple/ui-core/Display/LogoBackdrop";
import { displayNameForCarrier } from "@/Data/displayNameForCarrier";
import type { QuoteLike } from "@/Shared/Quotes/Types/QuoteLikeType";
import type { Broker } from "@freightsimple/generated-dashboard-openapi-client/models/Broker";
import type { CreditCard } from "@freightsimple/generated-dashboard-openapi-client/models/CreditCard";
import type { StateOfGoods } from "@freightsimple/generated-dashboard-openapi-client/models/StateOfGoods";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import type { BankAccount } from "@freightsimple/generated-dashboard-openapi-client/models/BankAccount";
import type { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import { QuotesColumn } from "@/Shared/Quotes/CustomComponents/QuotesColumn";
import { QuotesColumnHeader } from "@/Shared/Quotes/CustomComponents/QuotesColumnHeader";
import { QuotesColumnSectionHeader } from "@/Shared/Quotes/CustomComponents/QuotesColumnSectionHeader";
import { QuotesTextLink } from "@/Shared/Quotes/CustomComponents/QuotesTextLink";
import { QuotesModifyLink } from "@/Shared/Quotes/CustomComponents/QuotesModifyLink";
import { QuotesSecondaryText } from "@/Shared/Quotes/CustomComponents/QuotesSecondaryText";
import { getStateOfGoodsText } from "@/Helpers/getStateOfGoodsText";
import {
  BrokerSection_,
  PriceSection_,
} from "./Internal/quotesTransitColumnSections";

// The figure we quote platform-wide, and the one the quote PDF prints
// (QuoteDocumentService). It is not a fact about every carrier: liability is
// set by each carrier's own tariff, and the help centre says "typically $2 per
// pound or less". If per-carrier liability ever needs modelling, start there.
const maximumLiabilityDescription = "$2 / pound";

export interface TransitColumnProps {
  quote: QuoteLike;
  shipmentState: ShipmentState;
  proNumber: string | undefined;
  billOfLadingNumber?: string | undefined;
  broker?: Broker | undefined;
  showCarrierLogo?: boolean | undefined;
  showPriceSection?: boolean | undefined;
  price?: number | undefined;
  taxAmount?: number | undefined;
  currency?: string | undefined;
  priceBeforeCoupon?: number | undefined;
  selectedCard?: CreditCard | undefined;
  selectedBankAccount?: BankAccount | undefined;
  paymentTermsDays?: number | undefined;
  onChangePaymentMethod?: (() => void) | undefined;
  onChangeQuote?: (() => void) | undefined;
  onChangeBroker?: (() => void) | undefined;
  addInsuranceToShipment: boolean;
  insuranceAmount: number;
  insuranceCurrency: Currency;
  stateOfGoods: StateOfGoods;
  insurancePremiumAmount?: number | undefined;
  insurancePremiumCurrency?: string | undefined;
  insurancePremiumTaxesAmount?: number | undefined;
  insurancePremiumReferralReason?: string | undefined;
  insuranceCommodityGroup?: string | undefined;
}
function QuotesTransitColumn(props: TransitColumnProps) {
  const { quote, shipmentState } = props;

  const showCarrierLogo =
    props.showCarrierLogo ||
    !(
      shipmentState === ShipmentState.Quoted ||
      shipmentState === ShipmentState.QuoteRequested
    );

  const carrierDisplayName = displayNameForCarrier(quote.carrierIdentifier);
  const serviceDisplayName = quote.serviceDisplayName;

  /* const formatCommodityGroup = (commodityGroup: string) => {
    switch (commodityGroup) {
      case "NGM":
        return "New General Merchandise";
      case "UGM":
        return "Used General Merchandise";
      case "FRAG":
        return "Fragile Goods";
      case "NPG":
        return "Non-Perishable Goods";
      case "REF_OTHR":
        return "All Other Interests";
      default:
        return commodityGroup;
    }
  }; */

  function formatTransitDays(
    earliest: number | undefined,
    latest: number | undefined,
  ) {
    if (earliest === undefined) {
      return "Unknown";
    } else {
      if (latest === undefined || earliest === latest) {
        if (earliest === 1) {
          return `${earliest} Business Day`;
        } else {
          return `${earliest} Business Days`;
        }
      } else {
        return `${earliest}-${latest} Business Days`;
      }
    }
  }

  const transitTimeDescription = quote.transitBusinessDays
    ? `${formatTransitDays(
        quote.transitBusinessDays,
        quote.latestTransitBusinessDays,
      )}`
    : "Unknown";

  function describeInsurance() {
    return `$${props.insuranceAmount.toLocaleString()} ${
      props.insuranceCurrency
    }`;
  }

  const showInsurance = props.addInsuranceToShipment;
  const insuranceDescription = describeInsurance();

  return (
    <QuotesColumn>
      <div className="flex flex-col flex-nowrap items-start">
        <QuotesColumnHeader>Transit</QuotesColumnHeader>
        {props.proNumber && (
          <>
            <QuotesColumnSectionHeader>PRO Number</QuotesColumnSectionHeader>
            <QuotesSecondaryText>{props.proNumber}</QuotesSecondaryText>
          </>
        )}
        {props.billOfLadingNumber && (
          <>
            <QuotesColumnSectionHeader>BOL Number</QuotesColumnSectionHeader>
            <QuotesSecondaryText>
              {props.billOfLadingNumber}
            </QuotesSecondaryText>
          </>
        )}

        <QuotesColumnSectionHeader>Carrier</QuotesColumnSectionHeader>
        {showCarrierLogo && (
          <div className="my-2 flex flex-col flex-nowrap items-start">
            {/* The box, not the mark, gives the pad its 14px in dark mode. The
             * mark has to stay full size: half these logos are portrait
             * (dayross is 110x159), so the height binds and capping it shrinks
             * the artwork — the opposite of what this ticket asks for. The box
             * is a sibling of the link below in a flex column, so growing it
             * moves the link down rather than landing on it. Light is
             * untouched. */}
            <div className="flex h-16 w-40 flex-row items-center justify-start dark:h-[78px] dark:w-[174px]">
              <LogoBackdrop>
                <img
                  className="max-h-16 max-w-40"
                  alt={carrierDisplayName}
                  src={`/carrierLogos/${quote.carrierIdentifier}.png`}
                />
              </LogoBackdrop>
            </div>
          </div>
        )}
        {!showCarrierLogo && (
          <QuotesSecondaryText>{carrierDisplayName}</QuotesSecondaryText>
        )}
        {props.onChangeQuote && (
          <>
            <div className="h-2" />
            <QuotesModifyLink>
              <QuotesTextLink onClick={props.onChangeQuote}>
                Pick a different quote
              </QuotesTextLink>
            </QuotesModifyLink>
          </>
        )}

        <QuotesColumnSectionHeader>Service Type</QuotesColumnSectionHeader>
        <QuotesSecondaryText>{serviceDisplayName}</QuotesSecondaryText>
        <div className="h-1" />

        <PriceSection_ {...props} />

        <QuotesColumnSectionHeader>Transit Time</QuotesColumnSectionHeader>
        <QuotesSecondaryText>{transitTimeDescription}</QuotesSecondaryText>

        {!showInsurance && (
          <>
            <QuotesColumnSectionHeader>
              Maximum Liability
            </QuotesColumnSectionHeader>
            <QuotesSecondaryText>
              {maximumLiabilityDescription}
            </QuotesSecondaryText>
          </>
        )}
        {showInsurance && (
          <>
            <QuotesColumnSectionHeader>Insurance</QuotesColumnSectionHeader>
            <QuotesSecondaryText>{insuranceDescription}</QuotesSecondaryText>
            <QuotesColumnSectionHeader>
              State of Goods
            </QuotesColumnSectionHeader>

            {getStateOfGoodsText(props.stateOfGoods!)}
          </>
        )}

        {/*
        TODO: Re-enable once insurance api is ready for production
        {showInsurance && props.insuranceCommodityGroup && (
          <>
            <QuotesColumnSectionHeader>
              Insurance Commodity Group
              <CommodityGroupQuestionBubble />
            </QuotesColumnSectionHeader>
            <QuotesSecondaryText>
              {formatCommodityGroup(props.insuranceCommodityGroup)}
            </QuotesSecondaryText>
          </>
        )} */}

        <BrokerSection_ {...props} />
      </div>
    </QuotesColumn>
  );
}
export default QuotesTransitColumn;
